import { type ComponentProps, useMemo, useState } from 'react'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@cocoonspace/ui/components/command'
import { Input } from '@cocoonspace/ui/components/input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@cocoonspace/ui/components/popover'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@cocoonspace/ui/components/select'
import { Tabs, TabsList, TabsTrigger } from '@cocoonspace/ui/components/tabs'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import {
	CheckIcon,
	SearchIcon,
	TagIcon,
	UserRoundSearchIcon,
	XIcon,
} from 'lucide-react'
import {
	parseAsInteger,
	parseAsString,
	parseAsStringLiteral,
	useQueryState,
} from 'nuqs'

import { ErrorMsg } from '~/components/error-msg'
import { PageLoader } from '~/components/page-loader'
import { EventCard } from '~/domains/events/components/event-card/event-card'
import { EventYearFilter } from './event-year-filter'
import {
	type EventsByMonth,
	getFilteredEventsByMonth,
} from './get-filtered-events-by-month.util'
import { NoEventsMsg } from './no-events-msg'
import { type EventItem, useAllEvents } from './use-all-events.hook'

const useIncludeFilter = () =>
	useQueryState(
		'include',
		parseAsStringLiteral(['current', 'past']).withDefault('current'),
	)

export const useYearFilter = () => useQueryState('year', parseAsInteger)

const useSearchFilter = () =>
	useQueryState('search', parseAsString.withDefault(''))

const useTypeFilter = () =>
	useQueryState(
		'type',
		parseAsStringLiteral(['desk', 'meeting', 'visit', '']).withDefault(''),
	)

const ListGroupTitle = ({ children, ...props }: ComponentProps<'div'>) => (
	<h2
		{...props}
		className='sticky top-[50px] bg-gradient-to-b from-slate-100 from-70% py-6 pb-8 text-xl font-semibold capitalize'
	>
		{children}
	</h2>
)

const EventTypeFilter = () => {
	const { t } = useLingui()

	const [type, setType] = useTypeFilter()

	return (
		<Select
			value={type || 'all'}
			onValueChange={(value) =>
				setType(value === 'all' ? '' : (value as 'desk' | 'visit' | 'meeting'))
			}
		>
			<SelectTrigger
				inputSize='sm'
				variant='noBorder'
			>
				<SelectValue />
			</SelectTrigger>

			<SelectContent>
				<SelectItem value='all'>{t`Tous les types`}</SelectItem>
				<SelectItem value='meeting'>{t`Cocoons`}</SelectItem>
				<SelectItem value='visit'>{t`Visites`}</SelectItem>
			</SelectContent>
		</Select>
	)
}

const LabelFilter = ({
	events,
	labelIds,
	onLabelIdsChange,
}: {
	events: EventItem[]
	labelIds: string[]
	onLabelIdsChange: (labelIds: string[]) => void
}) => {
	const { t } = useLingui()
	const [open, setOpen] = useState(false)

	const uniqueLabels = useMemo(() => {
		const labelsMap = new Map<string, { id: string; name: string }>()
		for (const event of events) {
			for (const label of event.originalEvent?.labels ?? []) {
				if (label.id && label.name && !labelsMap.has(label.id)) {
					labelsMap.set(label.id, { id: label.id, name: label.name })
				}
			}
		}
		return Array.from(labelsMap.values()).sort((a, b) =>
			a.name.localeCompare(b.name),
		)
	}, [events])

	const toggleLabel = (id: string) => {
		onLabelIdsChange(
			labelIds.includes(id)
				? labelIds.filter((l) => l !== id)
				: [...labelIds, id],
		)
	}

	const displayValue = useMemo(() => {
		if (labelIds.length === 0) return ''
		if (labelIds.length === 1) {
			const label = uniqueLabels.find((l) => l.id === labelIds[0])
			return label?.name ?? labelIds[0]
		}
		return t`Labels (${labelIds.length})`
	}, [labelIds, uniqueLabels, t])

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
		>
			<PopoverTrigger asChild>
				<div className='group relative flex cursor-pointer items-center'>
					<TagIcon className='text-primary absolute left-3 size-4' />

					<Input
						aria-label={t`Filtrer par label`}
						inputSize='sm'
						className='!w-44 !rounded border-0 bg-white pl-9 text-ellipsis shadow-xs'
						placeholder={t`Filtrer par label`}
						value={displayValue}
						readOnly
					/>

					{labelIds.length > 0 && (
						<button
							type='button'
							className='text-primary absolute right-3'
							onClick={(e) => {
								e.stopPropagation()
								onLabelIdsChange([])
							}}
							aria-label={t`Effacer le filtre labels`}
						>
							<XIcon className='size-3' />
						</button>
					)}
				</div>
			</PopoverTrigger>

			<PopoverContent className='mt-1 w-72 p-0'>
				<Command>
					<CommandInput placeholder={t`Rechercher un label`} />

					<CommandList>
						<CommandEmpty>{t`Aucun résultat`}</CommandEmpty>

						<CommandGroup>
							{uniqueLabels.map((label) => {
								const isSelected = labelIds.includes(label.id)

								return (
									<CommandItem
										key={label.id}
										value={label.name}
										onSelect={() => toggleLabel(label.id)}
									>
										<div className='flex items-center gap-2'>
											<div
												className={cn(
													'border-primary flex size-4 items-center justify-center rounded-sm border',
													isSelected
														? 'bg-primary text-primary-foreground'
														: 'opacity-50 [&_svg]:invisible',
												)}
											>
												<CheckIcon className='size-3' />
											</div>

											<span>{label.name}</span>
										</div>
									</CommandItem>
								)
							})}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

const FilterByParticipants = ({
	events,
	guests,
	onGuestsChange,
}: {
	events: EventItem[]
	guests: string[]
	onGuestsChange: (guests: string[]) => void
}) => {
	const { t } = useLingui()
	const [open, setOpen] = useState(false)

	const uniqueGuests = useMemo(() => {
		const emails = new Set<string>()
		for (const event of events) {
			for (const guest of event.guests ?? []) {
				if (guest) {
					emails.add(guest)
				}
			}
		}
		return Array.from(emails).sort()
	}, [events])

	const toggleGuest = (email: string) => {
		onGuestsChange(
			guests.includes(email)
				? guests.filter((g) => g !== email)
				: [...guests, email],
		)
	}

	const displayValue = useMemo(() => {
		if (guests.length === 0) return ''
		if (guests.length === 1) return guests[0]
		return t`Participants (${guests.length})`
	}, [guests, t])

	return (
		<Popover
			open={open}
			onOpenChange={setOpen}
		>
			<PopoverTrigger asChild>
				<div className='group relative flex cursor-pointer items-center'>
					<UserRoundSearchIcon className='text-primary absolute left-4' />

					<Input
						aria-label={t`Filtrer par participant`}
						inputSize='sm'
						className='!w-48 !rounded border-0 bg-white pl-10 text-ellipsis shadow-xs'
						placeholder={t`Filtrer par participant`}
						value={displayValue}
						readOnly
					/>

					{guests.length > 0 && (
						<button
							type='button'
							className='text-primary absolute right-4'
							onClick={(e) => {
								e.stopPropagation()
								onGuestsChange([])
							}}
							aria-label={t`Effacer le filtre participants`}
						>
							<XIcon className='text-primary' />
						</button>
					)}
				</div>
			</PopoverTrigger>

			<PopoverContent className='mt-1 w-72 p-0'>
				<Command>
					<CommandInput placeholder={t`Rechercher un participant`} />

					<CommandList>
						<CommandEmpty>{t`Aucun résultat`}</CommandEmpty>

						<CommandGroup>
							{uniqueGuests.map((email) => {
								const isSelected = guests.includes(email)

								return (
									<CommandItem
										key={email}
										value={email}
										onSelect={() => toggleGuest(email)}
									>
										<div className='flex items-center gap-2'>
											<div
												className={cn(
													'border-primary flex size-4 items-center justify-center rounded-sm border',
													isSelected
														? 'bg-primary text-primary-foreground'
														: 'opacity-50 [&_svg]:invisible',
												)}
											>
												<CheckIcon className='size-3' />
											</div>

											<span>{email}</span>
										</div>
									</CommandItem>
								)
							})}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

const SearchEventsFilter = () => {
	const { t } = useLingui()

	const [search, setSearch] = useQueryState(
		'search',
		parseAsString.withDefault(''),
	)

	return (
		<div className='group relative flex items-center'>
			<SearchIcon className='text-primary absolute left-4' />

			<Input
				aria-label={t`Rechercher`}
				inputSize='sm'
				className='!w-36 !rounded border-0 bg-white pl-10 text-ellipsis shadow-xs'
				placeholder={t`Rechercher`}
				value={search}
				onChange={(e) => setSearch(e.target.value)}
			/>

			{search && (
				<button
					type='button'
					className='text-primary absolute right-4'
					onClick={() => setSearch(null)}
					aria-label={t`Effacer la recherche`}
				>
					<XIcon className='text-primary' />
				</button>
			)}
		</div>
	)
}

const EventsListTabs = ({
	events,
	eventsByMonth,
	guests,
	onGuestsChange,
	labelIds,
	onLabelIdsChange,
}: {
	events: EventItem[]
	eventsByMonth: EventsByMonth[]
	guests: string[]
	onGuestsChange: (guests: string[]) => void
	labelIds: string[]
	onLabelIdsChange: (labelIds: string[]) => void
}) => {
	const { t } = useLingui()

	const [include, setInclude] = useIncludeFilter()

	return (
		<div className='sticky top-0 z-30 flex flex-row items-center gap-8 bg-slate-100 pt-2'>
			<Tabs
				data-testid='events-list-tabs'
				value={include}
				onValueChange={(include) => {
					setInclude(include === 'past' ? 'past' : 'current')
				}}
			>
				<TabsList>
					<TabsTrigger value='current'>{t`À venir`}</TabsTrigger>
					<TabsTrigger value='past'>{t`Passés`}</TabsTrigger>
				</TabsList>
			</Tabs>

			<div className='ml-auto hidden shrink-0 items-end gap-2 md:flex'>
				<SearchEventsFilter />

				<FilterByParticipants
					events={events}
					guests={guests}
					onGuestsChange={onGuestsChange}
				/>

				<LabelFilter
					events={events}
					labelIds={labelIds}
					onLabelIdsChange={onLabelIdsChange}
				/>

				<EventTypeFilter />
				<EventYearFilter eventsByMonth={eventsByMonth} />
			</div>
		</div>
	)
}

export const EventsList = () => {
	const { t } = useLingui()
	const [include] = useIncludeFilter()
	const [search] = useSearchFilter()
	const [type] = useTypeFilter()
	const [year] = useYearFilter()
	const [guests, setGuests] = useState<string[]>([])
	const [labelIds, setLabelIds] = useState<string[]>([])

	const { events, isLoading, isError } = useAllEvents({
		include: include,
		year: year ?? undefined,
	})

	const eventsByMonth = useMemo(() => {
		return getFilteredEventsByMonth(events, {
			search,
			type,
			guests,
			labelIds,
		})
	}, [events, search, type, guests, labelIds])

	const sortedEventsByMonth = useMemo(() => {
		if (!eventsByMonth?.length) return eventsByMonth

		if (include === 'past') {
			return [...eventsByMonth].reverse()
		}

		return eventsByMonth
	}, [eventsByMonth, include])

	const hasNoEvents = !isLoading && !eventsByMonth?.length

	if (isError) {
		return (
			<ErrorMsg
				message={t`Une erreur est survenue lors de la récupération des événements`}
			/>
		)
	}

	return (
		<div>
			<EventsListTabs
				events={events}
				eventsByMonth={sortedEventsByMonth}
				guests={guests}
				onGuestsChange={setGuests}
				labelIds={labelIds}
				onLabelIdsChange={setLabelIds}
			/>

			{isLoading && (
				<div className='flex min-h-[500px] flex-1 justify-center'>
					<PageLoader />
				</div>
			)}

			{hasNoEvents && <NoEventsMsg />}

			{!isLoading &&
				sortedEventsByMonth?.map((item, index) => (
					<div
						key={item.month}
						id={item.month.replace(' ', '-')}
					>
						<ListGroupTitle style={{ zIndex: 2 + index }}>
							{item.month}
						</ListGroupTitle>

						<div className='grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
							{item.events?.map((event) => (
								<EventCard
									key={event.id}
									event={event as unknown as EventItem}
									isPast={include === 'past'}
								/>
							))}
						</div>
					</div>
				))}
		</div>
	)
}
